/**
 * Image Upload Handler for Foxy Fluff
 * Handles client-side image upload to Cloudflare R2 via Worker
 * Supports multiple upload zones (front/back) per product form
 */

(function () {
  'use strict';

  var WORKER_URL = 'https://uploader.fluffyfoxhome.com';
  var MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB
  var ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/heic'];

  // --- Safe DOM helpers ---

  function escapeHtml(text) {
    var div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Build a status message node with an SVG icon + text.
   * All user-supplied text is escaped via textContent.
   */
  function buildStatusNode(iconPath, colorClass, messageText) {
    var span = document.createElement('span');
    span.className = colorClass;

    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'inline w-4 h-4 mr-1');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('viewBox', '0 0 24 24');

    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('d', iconPath);
    svg.appendChild(path);

    span.appendChild(svg);
    span.appendChild(document.createTextNode(messageText));
    return span;
  }

  function buildSpinner() {
    var span = document.createElement('span');
    span.className = 'text-gray-600';

    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'inline w-4 h-4 mr-2 animate-spin');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');

    var circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('class', 'opacity-25');
    circle.setAttribute('cx', '12');
    circle.setAttribute('cy', '12');
    circle.setAttribute('r', '10');
    circle.setAttribute('stroke', 'currentColor');
    circle.setAttribute('stroke-width', '4');
    svg.appendChild(circle);

    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('class', 'opacity-75');
    path.setAttribute('fill', 'currentColor');
    path.setAttribute('d', 'M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z');
    svg.appendChild(path);

    span.appendChild(svg);
    span.appendChild(document.createTextNode('Uploading...'));
    return span;
  }

  // --- ImageUploader class ---

  function ImageUploader(container) {
    this.container = container;
    this.required = container.dataset.uploadRequired === 'true';
    this.uploaded = false;

    this.fileInput = container.querySelector('[data-upload-input]');
    this.statusEl = container.querySelector('[data-upload-status]');
    this.previewEl = container.querySelector('[data-upload-preview]');
    this.urlInput = container.querySelector('[data-upload-url]');
    this.thumbInput = container.querySelector('[data-upload-thumb]');

    if (this.fileInput) {
      var self = this;
      this.fileInput.addEventListener('change', function (e) {
        self.handleFileSelect(e);
      });
    }
  }

  ImageUploader.prototype.handleFileSelect = function (event) {
    var file = event.target.files[0];
    if (!file) return;

    var error = this.validateFile(file);
    if (error) {
      this.showError(error);
      return;
    }

    this.uploadFile(file);
  };

  ImageUploader.prototype.validateFile = function (file) {
    var fileType = file.type || getMimeFromExtension(file.name);
    if (ALLOWED_TYPES.indexOf(fileType) === -1) {
      return 'Invalid file type. Please upload a JPEG, PNG, or HEIC image.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large. Maximum size is 15MB.';
    }
    return null;
  };

  ImageUploader.prototype.uploadFile = function (file) {
    var self = this;
    self.showUploading();

    var contentType = file.type || getMimeFromExtension(file.name);

    fetch(WORKER_URL + '/presign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, contentType: contentType })
    })
      .then(function (presignResponse) {
        if (!presignResponse.ok) {
          return presignResponse.json().then(function (err) {
            throw new Error(err.error || 'Failed to prepare upload');
          });
        }
        return presignResponse.json();
      })
      .then(function (presignData) {
        return fetch(presignData.uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': contentType },
          body: file
        });
      })
      .then(function (uploadResponse) {
        if (!uploadResponse.ok) {
          return uploadResponse.json().then(function (err) {
            throw new Error(err.error || 'Upload failed');
          });
        }
        return uploadResponse.json();
      })
      .then(function (uploadData) {
        var thumbUrl = WORKER_URL + '/thumb/' + uploadData.key;

        self.urlInput.value = uploadData.publicUrl;
        self.thumbInput.value = thumbUrl;
        self.uploaded = true;

        self.showSuccess(file.name, thumbUrl);
        self.container.dispatchEvent(new CustomEvent('upload:change', { bubbles: true }));
      })
      .catch(function (error) {
        console.error('Upload error:', error);
        self.showError(error.message || 'Upload failed. Please try again.');
        self.reset();
      });
  };

  ImageUploader.prototype.showUploading = function () {
    this.statusEl.textContent = '';
    this.statusEl.appendChild(buildSpinner());
    this.fileInput.disabled = true;
  };

  ImageUploader.prototype.showSuccess = function (filename, thumbUrl) {
    this.statusEl.textContent = '';
    this.statusEl.appendChild(
      buildStatusNode('M5 13l4 4L19 7', 'text-green-600', 'Image uploaded: ' + filename)
    );
    this.fileInput.disabled = false;

    if (this.previewEl && thumbUrl) {
      this.previewEl.src = thumbUrl;
      this.previewEl.classList.remove('hidden');
    }
  };

  ImageUploader.prototype.showError = function (message) {
    this.statusEl.textContent = '';
    this.statusEl.appendChild(
      buildStatusNode('M6 18L18 6M6 6l12 12', 'text-red-600', message)
    );
    this.fileInput.disabled = false;
  };

  ImageUploader.prototype.reset = function () {
    this.urlInput.value = '';
    this.thumbInput.value = '';
    this.fileInput.value = '';
    this.uploaded = false;
    if (this.previewEl) {
      this.previewEl.classList.add('hidden');
      this.previewEl.removeAttribute('src');
    }
    this.container.dispatchEvent(new CustomEvent('upload:change', { bubbles: true }));
  };

  // --- Coordinator ---

  function initProductForm(form) {
    var zones = form.querySelectorAll('[data-upload-zone]');
    if (!zones.length) return;

    var uploaders = [];
    zones.forEach(function (zone) {
      uploaders.push(new ImageUploader(zone));
    });

    var addToCartBtn = form.querySelector('[data-add-to-cart]');
    var uploadHint = form.querySelector('[data-upload-hint]');

    function updateCartButton() {
      var allRequiredDone = uploaders.every(function (u) {
        return !u.required || u.uploaded;
      });
      if (addToCartBtn) {
        addToCartBtn.disabled = !allRequiredDone;
      }
      if (uploadHint) {
        uploadHint.style.display = allRequiredDone ? 'none' : 'block';
      }
    }

    form.addEventListener('upload:change', updateCartButton);
    updateCartButton();
  }

  // --- Helpers ---

  function getMimeFromExtension(filename) {
    var ext = filename.split('.').pop().toLowerCase();
    var mimeMap = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      heic: 'image/heic'
    };
    return mimeMap[ext] || '';
  }

  // --- Bootstrap ---

  function init() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach(initProductForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
